import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Truck, MapPin, Flame, ChevronRight, Calendar, Trophy, Utensils, Loader2 } from "lucide-react";

export default function FoodTruckDiscoverSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const params = {};
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
          );
          params.lat = pos.coords.latitude;
          params.lng = pos.coords.longitude;
        } catch { /* no GPS */ }
        const res = await axios.get(`${API}/trucks/discover-section`, { params });
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return null;
  if (!data?.has_trucks) {
    return (
      <div className="px-4 mb-5" data-testid="discover-trucks-empty">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
          <Truck className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm font-bold text-slate-400">No trucks in your area yet</p>
          <p className="text-[10px] text-slate-300 mt-1">Know a truck? Add them.</p>
          <button
            onClick={() => navigate("/submit")}
            className="mt-3 px-4 py-2 rounded-full bg-[#F59E0B] text-[#1C1C1E] text-xs font-bold"
            data-testid="submit-truck-btn"
          >
            Add a Truck
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5" data-testid="discover-trucks-section">
      {/* Section Header */}
      <div className="px-4 flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
          <Truck className="w-5 h-5 text-[#F59E0B]" />
          Food Trucks Near You
        </h2>
        <button
          onClick={() => navigate("/food-trucks")}
          className="text-xs text-[#F59E0B] font-bold flex items-center gap-0.5"
          data-testid="see-all-trucks"
        >
          See All <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Live Now / Going Live Soon */}
      {data.live_trucks.length > 0 ? (
        <div className="mb-3">
          <div className="px-4 flex items-center justify-between mb-2">
            <p className="text-[10px] text-green-600 font-bold uppercase tracking-wider flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Live Now
            </p>
            <button
              onClick={() => navigate("/live-map")}
              className="text-[10px] text-green-600 font-bold flex items-center gap-0.5 hover:underline"
              data-testid="open-live-map"
            >
              <MapPin className="w-3 h-3" /> Map View
            </button>
          </div>
          <div className="overflow-x-auto pb-2 px-4">
            <div className="flex gap-2.5">
              {data.live_trucks.map(truck => (
                <button
                  key={truck.business_id}
                  onClick={() => navigate(`/truck/${truck.business_id}`)}
                  className="min-w-[160px] max-w-[160px] bg-[#1C1C1E] rounded-xl p-3 text-left shrink-0 border border-white/5 hover:border-amber-500/30 transition"
                  data-testid={`live-truck-${truck.business_id}`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-[9px] text-green-400 font-bold">LIVE</span>
                  </div>
                  <p className="text-xs font-bold text-white truncate">{truck.name}</p>
                  <p className="text-[9px] text-white/40 truncate mt-0.5">
                    {(truck.cuisine_tags || []).join(", ")}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : data.scheduled_trucks.length > 0 ? (
        <div className="mb-3">
          <p className="px-4 text-[10px] text-amber-600 font-bold uppercase tracking-wider mb-2">Going Live Soon</p>
          <div className="overflow-x-auto pb-2 px-4">
            <div className="flex gap-2.5">
              {data.scheduled_trucks.map(truck => (
                <button
                  key={truck.business_id}
                  onClick={() => navigate(`/truck/${truck.business_id}`)}
                  className="min-w-[160px] max-w-[160px] bg-amber-50 rounded-xl p-3 text-left shrink-0 border border-amber-100"
                  data-testid={`scheduled-truck-${truck.business_id}`}
                >
                  <p className="text-xs font-bold text-slate-800 truncate">{truck.name}</p>
                  <p className="text-[9px] text-slate-400 truncate mt-0.5">
                    {(truck.cuisine_tags || []).join(", ")}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* HotSpots Teaser */}
      {data.top_hotspots.length > 0 && (
        <div className="px-4 mb-3">
          <div className="flex gap-2">
            {data.top_hotspots.map((hs, i) => (
              <button
                key={i}
                className="flex-1 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 text-left"
                data-testid={`hotspot-chip-${i}`}
              >
                <div className="flex items-center gap-1.5">
                  <Flame className="w-3 h-3 text-orange-500" />
                  <span className="text-[10px] font-bold text-orange-700 truncate">{hs.area_name}</span>
                </div>
                <p className="text-[9px] text-orange-400 mt-0.5">
                  {hs.active_now > 0 ? `${hs.active_now} live now` : `${hs.total_trucks} trucks`}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Birthday Crawl Planner CTA */}
      <div className="px-4 mb-3">
        <button
          onClick={() => navigate("/birthday-crawl")}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-3.5 text-left"
          data-testid="crawl-planner-cta"
        >
          <p className="text-sm font-black text-white">Plan Your Birthday Truck Crawl</p>
          <p className="text-[10px] text-white/70 mt-0.5">Pick your trucks, we'll optimize the route</p>
        </button>
      </div>

      {/* Leaderboard Top 3 Teaser */}
      {data.leaderboard_top3.length > 0 && (
        <div className="px-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <Trophy className="w-3 h-3 text-amber-500" /> Top Trucks This Month
            </p>
            <button onClick={() => navigate("/truck-leaderboard")} className="text-[10px] text-[#F59E0B] font-bold" data-testid="leaderboard-see-all">
              See All <ChevronRight className="w-2.5 h-2.5 inline" />
            </button>
          </div>
          <div className="flex gap-2">
            {data.leaderboard_top3.map((truck, i) => (
              <button
                key={truck.business_id}
                onClick={() => navigate(`/truck/${truck.business_id}`)}
                className="flex-1 bg-white rounded-xl border border-slate-100 p-2 text-center"
                data-testid={`lb-teaser-${i}`}
              >
                <div className={`w-5 h-5 rounded-full mx-auto mb-1 flex items-center justify-center text-[9px] font-black text-white ${
                  i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-400" : "bg-amber-700"
                }`}>{i + 1}</div>
                <p className="text-[10px] font-bold text-slate-800 truncate">{truck.name}</p>
                <p className="text-[8px] text-slate-400">{truck.score} pts</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Events Teaser */}
      {data.next_events.length > 0 && (
        <div className="px-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <Calendar className="w-3 h-3 text-blue-500" /> Upcoming Events
            </p>
          </div>
          {data.next_events.map((event, i) => {
            const isPredictiq = event.created_by === "predictiq";
            const highDemand = isPredictiq && event.demand_score > 0.7;
            return (
              <div key={event.event_id || i} className="bg-white rounded-xl border border-slate-100 p-3 mb-2" data-testid={`event-teaser-${i}`}>
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-bold text-slate-800 flex-1">{event.event_name}</p>
                  {highDemand && (
                    <span className="text-[9px] text-orange-600 font-bold flex items-center gap-0.5" data-testid="high-demand-badge">
                      High demand
                    </span>
                  )}
                  {isPredictiq && (
                    <span className="text-[8px] text-blue-400 bg-blue-50 px-1.5 py-0.5 rounded-full font-medium" data-testid="predicted-badge">
                      Predicted
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {event.location_name} {event.next_occurrence ? `— ${new Date(event.next_occurrence).toLocaleDateString()}` : ""}
                </p>
                {isPredictiq && event.predicted_attendance > 0 && (
                  <p className="text-[9px] text-slate-300 mt-0.5" data-testid="predicted-attendance">
                    Estimated {event.predicted_attendance.toLocaleString()} people attending
                  </p>
                )}
                {(event.confirmed_truck_details || []).length > 0 && (
                  <p className="text-[9px] text-amber-600 mt-1">
                    {event.confirmed_truck_details.length} truck{event.confirmed_truck_details.length !== 1 ? "s" : ""} confirmed
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
